import { Injectable, inject } from '@angular/core';

import { filter, map, mergeMap, switchMap, tap } from 'rxjs';

import { CamDynamicTranslationService } from '@camelot/core';
import {
  CamBaseService,
  GraphEndpoint,
  GraphReponsePaged,
  HandleComplexRequest,
  HandleSimpleRequest,
  createQuery,
  graphAggregatorEndpoint,
} from '@camelot/server';
import { addressProps } from '@camelot/services';
import { isNonNullable } from '@camelot/utils';

import { Activity, activityProps } from './dto/activity';
import { ActivityCreationPayloadInput } from './dto/activity-creation-payload-input';
import { ActivityModifierPayloadInput } from './dto/activity-modifier-payload-input';
import { Company, companyProps } from './dto/company';
import { CompanyCreationPayloadInput } from './dto/company-creation-payload-input';
import { CompanyModifierPayloadInput } from './dto/company-modifier-payload-input';
import { Department, departmentProps } from './dto/department';
import { DepartmentCreationPayloadInput } from './dto/department-creation-payload-input';
import { DepartmentModifierPayloadInput } from './dto/department-modifier-payload-input';
import {
  CREATE_ACTIVITY,
  CREATE_COMPANY,
  CREATE_DEPARTMENT,
  UPDATE_ACTIVITY,
  UPDATE_COMPANY,
  UPDATE_DEPARTMENT,
} from './mutations';
import { GET_DEPARTMENTS } from './queries';

const graphEndpoint: GraphEndpoint = {
  clientName: 'organizationService',
  endpoint: 'organization',
};

@Injectable({
  providedIn: 'root',
})
export class CamOrganizationsService extends CamBaseService {
  protected _graphEndpoint = graphEndpoint;

  private readonly _dynamicTranslationService = inject(CamDynamicTranslationService);

  public companyById = new HandleComplexRequest<Company>();
  public companies = new HandleSimpleRequest<Company[]>();

  public department = new HandleComplexRequest<Department>();
  public departments = new HandleSimpleRequest<Department[]>();

  public activityById = new HandleComplexRequest<Activity>();
  public activities = new HandleSimpleRequest<Activity[]>();

  constructor() {
    super();
    super.registerRoutes({ graphEndpoint: graphEndpoint });
  }

  public fetchDepartments$() {
    return this.departments.fetch(
      this._graphService
        .fetchPagedQueryList<Department>(
          GET_DEPARTMENTS(
            ``,
            `
              ${departmentProps.get('id')}
              ${departmentProps.get('name')}
              `
          ),
          'departments',
          graphEndpoint.clientName
        )
        .pipe(map(data => data.items, filter(isNonNullable)))
    );
  }

  public fetchDepartment$(id: string) {
    return this.department.fetch(
      id,
      this._graphService
        .fetchPagedQueryList<Department>(
          GET_DEPARTMENTS(
            `where: { id: { eq: "${id}" }}`,
            `
              ${departmentProps.get('id')}
              ${departmentProps.get('name')}
              `
          ),
          'departments',
          graphEndpoint.clientName
        )
        .pipe(
          map(data => (data.items && data.items.length === 1 ? data.items[0] : null)),
          filter(isNonNullable)
        )
    );
  }

  public fetchDepartmentsByIds$(ids: string[]) {
    return this._graphService
      .fetchPagedQueryList<Department>(
        GET_DEPARTMENTS(
          `where: { id: { in: [${ids.map(id => `"${id}"`).join(', ')}] } }`,
          `
                ${departmentProps.get('id')}
                ${departmentProps.get('name')}
              `
        ),
        'departments',
        graphEndpoint.clientName
      )
      .pipe(
        filter(isNonNullable),
        map(data => data.items ?? [])
      );
  }

  public fetchCompanies$() {
    return this.companies.fetch(
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<Company>>(
          createQuery<Company>('companies', {
            prefixType: 'Company',
            props: `
              items {
                ${companyProps.get('id')}
                ${companyProps.get('name')}
                ${companyProps.get('vatNumber')}
                ${companyProps.get('address')} {
                  ${addressProps.get('id')}
                  ${addressProps.get('street')}
                  ${addressProps.get('complement')}
                  ${addressProps.get('city')}
                  ${addressProps.get('postalCode')}
                  ${addressProps.get('countryCode')}
                }
              }
            `,
          }),
          graphAggregatorEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public fetchCompany$(id: string) {
    return this.companyById.fetch(
      id,
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<Company>>(
          createQuery<Company>('companies', {
            where: { id: { eq: id } },
            prefixType: 'Company',
            props: `
              items {
                ${companyProps.get('id')}
                ${companyProps.get('name')}
                ${companyProps.get('vatNumber')}
                ${companyProps.get('pictureDocumentId')}
                ${companyProps.get('pictureUrl')}
                ${companyProps.get('addressId')}
                ${companyProps.get('address')} {
                  ${addressProps.get('id')}
                  ${addressProps.get('street')}
                  ${addressProps.get('complement')}
                  ${addressProps.get('city')}
                  ${addressProps.get('postalCode')}
                  ${addressProps.get('countryCode')}
                }
              }
            `,
          }),
          graphAggregatorEndpoint.clientName
        )
        .pipe(
          map(data => (data.items && data.items.length === 1 ? data.items[0] : null)),
          filter(isNonNullable)
        )
    );
  }

  public fetchCompaniesByIds$(ids: string[]) {
    return this._graphService
      .fetchQueryBuilder<GraphReponsePaged<Company>>(
        createQuery<Company>('companies', {
          where: { id: { in: ids } },
          prefixType: 'Company',
          props: `
            items {
              ${companyProps.get('id')}
              ${companyProps.get('name')}
            }
          `,
        }),
        graphEndpoint.clientName
      )
      .pipe(
        filter(isNonNullable),
        map(data => data.items ?? [])
      );
  }

  public createCompany$(payload: Partial<CompanyCreationPayloadInput>) {
    return this._graphService
      .mutateBuilder<Company>(CREATE_COMPANY(payload), graphEndpoint.clientName, ['companies'])
      .pipe(
        filter(isNonNullable),
        tap(company => {
          this.companyById.add(company.id, company);
          const current = this.companies.data$.getValue() ?? [];
          this.companies.data$.next([...current, company]);
        })
      );
  }

  public updateCompany$(payload: Partial<CompanyModifierPayloadInput>) {
    return this._graphService
      .mutateBuilder<Company>(UPDATE_COMPANY(payload), graphEndpoint.clientName, ['companies'])
      .pipe(
        filter(isNonNullable),
        tap(company => {
          this.companyById.update(company.id, company, true);
          const current = this.companies.data$.getValue() ?? [];
          this.companies.data$.next(current.map(c => (c.id === company.id ? { ...c, ...company } : c)));
        }),
        switchMap(company => this.fetchCompany$(company.id).pipe(map(() => company)))
      );
  }

  public createDepartment$(payload: DepartmentCreationPayloadInput) {
    return this._graphService
      .mutateBuilder<Department>(CREATE_DEPARTMENT(payload), graphEndpoint.clientName, ['departments'])
      .pipe(
        filter(isNonNullable),
        tap(department => {
          this.department.add(department.id, department);
          const current = this.departments.data$.getValue() ?? [];
          this.departments.data$.next([...current, department]);
        })
      );
  }

  public updateDepartment$(payload: Partial<DepartmentModifierPayloadInput>) {
    return this._graphService
      .mutateBuilder<Department>(UPDATE_DEPARTMENT(payload), graphEndpoint.clientName, ['departments'])
      .pipe(
        filter(isNonNullable),
        tap(department => {
          this.department.update(department.id, department, true);
          const current = this.departments.data$.getValue() ?? [];
          this.departments.data$.next(current.map(d => (d.id === department.id ? { ...d, ...department } : d)));
        })
      );
  }

  public fetchActivities$(enabled?: boolean) {
    return this.activities.fetch(
      this._graphService
        .fetchQueryBuilder<GraphReponsePaged<Activity>>(
          createQuery<Activity>('activities', {
            prefixType: 'Activity',
            where: isNonNullable(enabled) ? { enabled: { eq: enabled } } : null,
            props: `
              items {
                ${activityProps.get('id')}
                ${activityProps.get('code')}
                ${activityProps.get('enabled')}
                ${activityProps.get('descriptionTranslationKey')}
              }
            `,
          }),
          graphEndpoint.clientName
        )
        .pipe(
          filter(isNonNullable),
          map(data => data.items ?? [])
        )
    );
  }

  public createActivity$(payload: ActivityCreationPayloadInput) {
    return this._graphService
      .mutateBuilder<Activity>(CREATE_ACTIVITY(payload), graphEndpoint.clientName, ['activities'])
      .pipe(
        filter(isNonNullable),
        tap(activity => {
          this.activityById.add(activity.id, activity);
          const current = this.activities.data$.getValue() ?? [];
          this.activities.data$.next([...current, activity]);
        }),
        mergeMap(activity => this._dynamicTranslationService.reload().pipe(map(() => activity)))
      );
  }

  public updateActivity$(payload: Partial<ActivityModifierPayloadInput>) {
    return this._graphService
      .mutateBuilder<Activity>(UPDATE_ACTIVITY(payload), graphEndpoint.clientName, ['activities'])
      .pipe(
        filter(isNonNullable),
        tap(activity => {
          this.activityById.update(activity.id, activity, true);
          const current = this.activities.data$.getValue() ?? [];
          this.activities.data$.next(current.map(a => (a.id === activity.id ? { ...a, ...activity } : a)));
        }),
        mergeMap(activity => this._dynamicTranslationService.reload().pipe(map(() => activity)))
      );
  }
}
